import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './RSIConnors.css';
import './ReversalMonitor.css';
import { getReversalMonitor } from '../api/brapi';
import { addPosition, getPortfolio } from '../api/portfolio';
import DismissibleNotice from './DismissibleNotice';
import EstatisticasHonestas from './EstatisticasHonestas';
import ForaDaAmostra from './ForaDaAmostra';
import EstagioEstrategia from './EstagioEstrategia';
import PortfolioModal from './PortfolioModal';
import ReversalSetupChart from './ReversalSetupChart';
import DirectionalOptionsReader from './DirectionalOptionsReader';
import { formatTradingDate } from '../utils/dates';
import {
  formatBacktestPercent,
  formatProfitFactor,
  formatStrategyHealthBanner,
  formatWeightedProfitFactor,
} from '../utils/backtestRisk';

const STATUS_ORDER = {
  DISPARADO: 0,
  ARMADO: 1,
  REVISAR: 2,
  PRÓXIMO: 3,
};

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatPrice(value) {
  const number = finiteNumber(value);
  if (number == null) return '—';
  return `R$\u202F${number.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatPct(value, sign = true) {
  const number = finiteNumber(value);
  if (number == null) return '—';
  const prefix = sign && number > 0 ? '+' : '';
  return `${prefix}${number.toFixed(2).replace('.', ',')}%`;
}

function formatAtr(value) {
  const number = finiteNumber(value);
  return number == null ? '—' : `${number.toFixed(2).replace('.', ',')} ATR`;
}

function formatVolume(value) {
  const number = finiteNumber(value);
  if (number == null) return '—';
  if (number >= 1_000_000_000) return `R$ ${(number / 1_000_000_000).toFixed(2).replace('.', ',')} bi`;
  return `R$ ${(number / 1_000_000).toFixed(1).replace('.', ',')} mi`;
}

function formatScore(value, max = 100) {
  const score = finiteNumber(value);
  return score == null ? '—' : `${score}/${max}`;
}

function conditionPass(condition) {
  return condition?.pass !== false && condition?.passed !== false;
}

function conditionLabel(condition) {
  if (typeof condition === 'string') return condition;
  return condition?.label || condition?.name || condition?.key || 'Condição';
}

function compactConditionLabel(condition) {
  const key = condition?.key || condition?.id;
  return {
    trend: 'Tendência',
    liquidity: 'Liquidez',
    trigger: 'Gatilho',
    fired: 'Rompimento',
    riskReward: 'R/R ≥ 1,5',
  }[key] || conditionLabel(condition);
}

function conditionValue(condition) {
  if (typeof condition === 'string') return '—';
  return condition?.observed ?? condition?.value ?? condition?.actual ?? '—';
}

function conditionPoints(condition) {
  if (typeof condition === 'string') return '—';
  const points = condition?.points ?? condition?.score;
  const weight = condition?.weight ?? condition?.maxPoints ?? condition?.max;
  if (points == null && weight == null) return '—';
  return `${points ?? 0}/${weight ?? '—'} pts`;
}

function itemBlocks(item) {
  if (Array.isArray(item?.blocks)) return item.blocks;
  return item?.blocks ? [item.blocks] : [];
}

function blockLabel(block) {
  return typeof block === 'string'
    ? block
    : block?.reason || block?.label || block?.code || 'Restrição ativa';
}

function hasUnfavorableRiskReward(item) {
  const riskReward = finiteNumber(item?.riskReward);
  return riskReward != null && riskReward < 1;
}

function statusLabel(item) {
  if (item?.status === 'DISPARADO') return 'DISPARADO';
  if (item?.status === 'ARMADO') return 'ARMADO';
  if (item?.status === 'REVISAR' || item?.needsReview) return 'REVISAR';
  return 'PRÓXIMO';
}

function statusClass(item) {
  if (itemBlocks(item).length > 0 || hasUnfavorableRiskReward(item) || item?.needsReview) return 'blocked';
  if (item?.status === 'DISPARADO') return 'triggered';
  if (item?.status === 'ARMADO') return 'armed';
  return 'candidate';
}

function setupStatusText(item) {
  if (!item) return '';
  if (item.trigger) return item.trigger;
  if (item.status === 'DISPARADO') {
    const reference = item.referenceHigh != null ? `máxima ${formatPrice(item.referenceHigh)} superada` : 'rompimento confirmado';
    return `Disparado — ${reference}`;
  }
  if (item.status === 'ARMADO') {
    return `Armado — aguardando rompimento de ${formatPrice(item.entryPrice)}`;
  }
  if (item.status === 'REVISAR' || item.needsReview) {
    return 'Revisar data ex antes de considerar uma entrada';
  }
  return `Próximo — RSI(2) em ${finiteNumber(item.rsi2)?.toFixed(1) ?? '—'}`;
}

function isReversalPosition(position) {
  const strategy = String(position?.strategy || position?.source_strategy || '').toLowerCase();
  return strategy === 'reversal' || strategy.includes('reversão') || strategy.includes('reversao');
}

function positionState(position) {
  return String(position?.order_status || position?.status || '').toLowerCase();
}

function ScoreCircle({ score, max = 100 }) {
  const numericScore = Math.max(0, Math.min(max, finiteNumber(score) ?? 0));
  const ratio = max > 0 ? numericScore / max : 0;
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const color = numericScore >= 85 ? '#26a69a' : numericScore >= 70 ? '#facc15' : numericScore >= 50 ? '#f97316' : '#ef5350';
  return (
    <div className="rc-score-circle">
      <svg viewBox="0 0 110 110" width="110" height="110" aria-hidden="true">
        <circle cx="55" cy="55" r={radius} fill="none" stroke="#1c2232" strokeWidth="7" />
        <circle
          cx="55"
          cy="55"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
          strokeLinecap="round"
          transform="rotate(-90 55 55)"
        />
      </svg>
      <div className="rc-score-inner">
        <div className="rc-score-num rv-score-num" style={{ color }}>{formatScore(numericScore, max)}</div>
        <div className="rc-score-lbl">Setup</div>
      </div>
    </div>
  );
}

function PositionList({ title, items, emptyText, onSelect }) {
  return (
    <div className="rc-panel">
      <div className="rc-panel-title">{title} · {items.length}</div>
      {items.length === 0 ? (
        <div className="rc-empty-pos">{emptyText}</div>
      ) : items.map(position => {
        const pnl = finiteNumber(position.pnl_pct);
        return (
          <button
            type="button"
            className="rc-position-item rv-position-button"
            key={position.id || `${title}-${position.ticker}`}
            onClick={() => onSelect(position.ticker)}
          >
            <div className="rc-pos-header">
              <span className="rc-pos-ticker">{position.ticker}</span>
              {pnl != null && (
                <span className={`rc-pos-change ${pnl >= 0 ? 'up' : 'down'}`}>
                  {formatPct(pnl)}
                </span>
              )}
            </div>
            <div className="rc-pos-row"><span>Entrada</span><span>{formatPrice(position.entry_price || position.trigger_price)}</span></div>
            {position.current_price != null && (
              <div className="rc-pos-row"><span>Atual</span><span>{formatPrice(position.current_price)}</span></div>
            )}
            {position.stop_price != null && (
              <div className="rc-pos-row"><span>Stop</span><span>{formatPrice(position.stop_price)}</span></div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ReversalTable({ items, selectedTicker, onSelect }) {
  const [sort, setSort] = useState({ key: 'status', direction: 'asc' });
  const sortedItems = useMemo(() => [...items].sort((left, right) => {
    let leftValue;
    let rightValue;
    if (sort.key === 'status') {
      leftValue = STATUS_ORDER[statusLabel(left)] ?? 99;
      rightValue = STATUS_ORDER[statusLabel(right)] ?? 99;
    } else {
      leftValue = left?.[sort.key];
      rightValue = right?.[sort.key];
    }
    const leftNumber = finiteNumber(leftValue);
    const rightNumber = finiteNumber(rightValue);
    const comparison = leftNumber != null && rightNumber != null
      ? leftNumber - rightNumber
      : String(leftValue ?? '').localeCompare(String(rightValue ?? ''), 'pt-BR');
    return sort.direction === 'asc' ? comparison : -comparison;
  }), [items, sort]);

  const setSortKey = key => {
    setSort(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const header = (key, label, alignment = '') => (
    <th className={alignment}>
      <button type="button" className="rv-sort-button" onClick={() => setSortKey(key)}>
        {label}{sort.key === key ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  );

  return (
    <section className="rc-scanner rv-table-section">
      <div className="rc-scanner-header">
        <div>
          <div className="rc-scanner-title">Elegíveis · universo completo</div>
          <div className="rc-scanner-sub">Selecione um ativo ou ordene qualquer coluna.</div>
        </div>
        <strong className="rv-table-count">{items.length}</strong>
      </div>
      {items.length === 0 ? (
        <div className="rv-empty">Nenhum ativo elegível no último pregão.</div>
      ) : (
        <table className="rc-table rv-table">
          <thead>
            <tr>
              {header('ticker', 'Ativo')}
              {header('close', 'Fechamento', 'right')}
              {header('rsi2', 'RSI(2)', 'right')}
              {header('distEma21Pct', 'Dist. MME21', 'right')}
              {header('distSma200Pct', 'Dist. SMA200', 'right')}
              {header('entryPrice', 'Entrada', 'right')}
              {header('stopPrice', 'Stop', 'right')}
              {header('atr14', 'ATR(14)', 'right')}
              {header('riskPct', 'Risco %', 'right')}
              {header('riskAtr', 'Risco ATR', 'right')}
              {header('targetAtr', 'Alvo ATR', 'right')}
              {header('target1Price', 'Alvo 1', 'right')}
              {header('riskReward', 'R/R', 'right')}
              {header('avgFinancialVolume21', 'Vol. 21d', 'right')}
              {header('status', 'Status', 'center')}
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(item => {
              const status = statusLabel(item);
              const blocked = itemBlocks(item).length > 0 || hasUnfavorableRiskReward(item) || item.needsReview;
              return (
                <tr
                  key={`${status}-${item.ticker}`}
                  className={selectedTicker === item.ticker ? 'selected' : ''}
                  onClick={() => onSelect(item.ticker)}
                  data-testid={`row-reversal-${item.ticker}-${item.status || 'candidate'}`}
                >
                  <td data-label="Ativo">
                    <div className="rc-ticker-cell">
                      <span className="rc-ticker-sym">{item.ticker}</span>
                      <span className="rc-ticker-name">{item.name || item.sector || 'Ativo B3'}</span>
                    </div>
                  </td>
                  <td data-label="Fechamento" className="right rc-mono">{formatPrice(item.close)}</td>
                  <td data-label="RSI(2)" className="right rc-mono">{finiteNumber(item.rsi2)?.toFixed(1) ?? '—'}</td>
                  <td data-label="Dist. MME21" className="right rc-mono">{formatPct(item.distEma21Pct)}</td>
                  <td data-label="Dist. SMA200" className="right rc-mono">{formatPct(item.distSma200Pct)}</td>
                  <td data-label="Entrada" className="right rc-mono">{formatPrice(item.entryPrice)}</td>
                  <td data-label="Stop" className="right rc-mono">{formatPrice(item.stopPrice)}</td>
                  <td data-label="ATR(14)" className="right rc-mono">{formatPrice(item.atr14)}</td>
                  <td data-label="Risco %" className="right rc-mono">{formatPct(item.riskPct)}</td>
                  <td data-label="Risco ATR" className="right rc-mono">{formatAtr(item.riskAtr)}</td>
                  <td data-label="Alvo ATR" className="right rc-mono">{formatAtr(item.targetAtr)}</td>
                  <td data-label="Alvo 1" className="right rc-mono">{formatPrice(item.target1Price)}</td>
                  <td data-label="R/R" className="right rc-mono">{finiteNumber(item.riskReward) == null ? '—' : `${Number(item.riskReward).toFixed(2).replace('.', ',')}R`}</td>
                  <td data-label="Vol. 21d" className="right rc-mono">{formatVolume(item.avgFinancialVolume21)}</td>
                  <td data-label="Status" className="center">
                    <span className={`rc-action-btn rv-status ${statusClass(item)}`}>
                      {blocked && <span aria-hidden="true">× </span>}
                      {blocked && hasUnfavorableRiskReward(item)
                        ? <span>R/R &lt; 1 — não operar</span>
                        : status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default function ReversalMonitor({ focusTicker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [optionsExpanded, setOptionsExpanded] = useState(false);
  const [entryVia, setEntryVia] = useState('ativo');

  const allItems = useMemo(() => {
    const unique = new Map();
    [
      ...(data?.triggered || []),
      ...(data?.armed || []),
      ...(data?.reviews || []),
      ...(data?.candidates || []),
    ].forEach(item => {
      if (!unique.has(item.ticker)) unique.set(item.ticker, item);
    });
    return [...unique.values()];
  }, [data]);

  const selected = useMemo(
    () => allItems.find(item => item.ticker === selectedTicker) || allItems[0] || null,
    [allItems, selectedTicker],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getReversalMonitor();
      setData(result);
      const items = [
        ...(result.triggered || []),
        ...(result.armed || []),
        ...(result.reviews || []),
        ...(result.candidates || []),
      ];
      setSelectedTicker(current => {
        if (current && items.some(item => item.ticker === current)) return current;
        return items.find(item => item.ticker === focusTicker)?.ticker
          || items[0]?.ticker
          || null;
      });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Falha ao carregar o monitor de reversão.');
    } finally {
      setLoading(false);
    }
  }, [focusTicker]);

  const loadPortfolio = useCallback(async () => {
    try {
      const result = await getPortfolio();
      setPortfolio(result?.positions || []);
    } catch (err) {
      if (process.env.NODE_ENV !== 'test') console.error('[ReversalMonitor] portfolio load error:', err);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    loadPortfolio();
    const id = setInterval(loadPortfolio, 30000);
    return () => clearInterval(id);
  }, [loadPortfolio]);

  useEffect(() => {
    const reload = () => load();
    window.addEventListener('fatcat:data-reload', reload);
    return () => window.removeEventListener('fatcat:data-reload', reload);
  }, [load]);

  useEffect(() => {
    if (focusTicker && allItems.some(item => item.ticker === focusTicker)) setSelectedTicker(focusTicker);
  }, [allItems, focusTicker]);

  const persistedBacktest = data?.backtest || data?.backtestSummary || null;
  const backtestGate = data?.riskBacktest || data?.backtestGate || persistedBacktest?.gate || {};
  const backtestCostsBps = finiteNumber(persistedBacktest?.costsBps ?? backtestGate?.costsBps);
  const hasBacktestCosts = backtestCostsBps != null && backtestCostsBps > 0;
  const netExpectancyPct = hasBacktestCosts
    ? (persistedBacktest?.netExpectancyPct ?? backtestGate?.netExpectancyPct)
    : null;
  const weightedNetExpectancyPct = hasBacktestCosts
    ? (persistedBacktest?.weightedNetExpectancyPct ?? backtestGate?.weightedNetExpectancyPct)
    : null;
  const netExpectancyLabel = hasBacktestCosts
    ? `Expectância líquida (${backtestCostsBps} bps/lado)`
    : 'Expectância líquida';
  const netExpectancySummary = finiteNumber(netExpectancyPct) == null
    ? netExpectancyLabel
    : `${netExpectancyLabel} ${formatBacktestPercent(netExpectancyPct)}`;
  const healthUnavailable = backtestGate.available === false && !persistedBacktest;
  const backtestBlocked = healthUnavailable || backtestGate.blocked === true || persistedBacktest?.blocked === true;
  const shortBacktest = !healthUnavailable && finiteNumber(backtestGate.tradingDays ?? persistedBacktest?.tradingDays) != null
    && Number(backtestGate.tradingDays ?? persistedBacktest?.tradingDays) < 252;
  const selectedBlocks = itemBlocks(selected);
  const portfolioBlocked = selectedBlocks.length > 0 || hasUnfavorableRiskReward(selected) || selected?.needsReview;
  const portfolioBlockReason = selectedBlocks.length > 0
    ? `Entrada bloqueada: ${selectedBlocks.map(blockLabel).join(' · ')}`
    : backtestBlocked
      ? `Entrada bloqueada: backtest da estratégia não autoriza novas operações${hasBacktestCosts ? ` · ${netExpectancySummary}` : ''}`
      : selected?.needsReview
        ? 'Revise a data ex antes de considerar uma entrada'
        : hasUnfavorableRiskReward(selected)
          ? 'Setup bloqueado: risco/retorno abaixo de 1'
          : 'Adicionar à carteira';

  const reversalPortfolio = useMemo(() => portfolio.filter(isReversalPosition), [portfolio]);
  const openPositions = reversalPortfolio.filter(position => positionState(position) === 'open');
  const pendingOrders = reversalPortfolio.filter(position => positionState(position) === 'pending');
  const conditions = Array.isArray(selected?.conditions) ? selected.conditions : [];
  const passedConditions = conditions.filter(conditionPass).length;
  const kpis = data?.kpis || {};
  const rrBlockedCount = allItems.filter(hasUnfavorableRiskReward).length;
  const monitored = kpis.total ?? allItems.length;
  const eligible = kpis.eligible ?? allItems.length;

  if (loading && !data) {
    return (
      <div className="rc-root rv-root" data-testid="status-reversal-loading">
        <div className="rc-loading">
          <span className="rc-spinner" />
          <strong>Calculando reversões a favor da tendência</strong>
          <span>Conferindo tendência, liquidez e gatilhos do último pregão.</span>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rc-root rv-root" data-testid="status-reversal-error">
        <div className="rc-error-msg rv-error">
          <span>Não foi possível carregar o monitor: {error}</span>
          <button type="button" onClick={load} data-testid="button-retry-reversal">Tentar novamente</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rc-root rv-root">
      <div className="rv-data-banner">
        <span data-testid="text-reversal-reference-date">Referência: {formatTradingDate(data?.referenceDate)}</span>
      </div>

      {!healthUnavailable && (
        <DismissibleNotice noticeKey="reversal-backtest-risk" eventKey={backtestBlocked ? 'blocked' : 'released'} closeLabel="Fechar aviso de saúde da estratégia de reversão" className="rc-backtest-risk-banner rv-risk-banner" role={backtestBlocked ? 'alert' : 'status'} data-testid="reversal-backtest-risk-banner">
          {formatStrategyHealthBanner(backtestGate)}
        </DismissibleNotice>
      )}
      <div className="rc-backtest-sample-warning rv-sample-warning" role="note">
        Bloqueio reavaliado diariamente com o backtest ponderado (meia-vida 126 pregões).
      </div>
      {shortBacktest && !backtestBlocked && (
        <DismissibleNotice noticeKey="reversal-short-sample" eventKey="short-sample" closeLabel="Fechar aviso de amostra curta" className="rc-backtest-sample-warning rv-sample-warning" role="status">
          Amostra curta: {backtestGate.tradingDays ?? persistedBacktest?.tradingDays} pregões
        </DismissibleNotice>
      )}
      {error && <DismissibleNotice noticeKey="reversal-refresh-error" eventKey={error} closeLabel="Fechar erro de atualização da reversão" className="rv-inline-error" role="alert" data-testid="status-reversal-refresh-error">{error}</DismissibleNotice>}

      <section className="rc-kpi-strip rv-kpi-strip" aria-label="Resumo do monitor">
        {[
          ['Monitorados', monitored, ''],
          ['Elegíveis', eligible, ''],
          ['Armados', kpis.armed ?? data?.armed?.length ?? 0, (kpis.armed ?? data?.armed?.length) > 0 ? 'warn' : ''],
          ['Disparados', kpis.triggered ?? data?.triggered?.length ?? 0, (kpis.triggered ?? data?.triggered?.length) > 0 ? 'up' : ''],
          ['Revisar data ex', kpis.review ?? data?.reviews?.length ?? 0, (kpis.review ?? data?.reviews?.length) > 0 ? 'warn' : ''],
          ['R/R < 1', rrBlockedCount, rrBlockedCount > 0 ? 'down' : ''],
          // FAT-12: papéis com indicador não pronto ficam fora das listas de sinal e só entram nesta contagem.
          ['Dados insuficientes', kpis.insufficientData ?? 0, ''],
          ['Expectância ponderada bruta', persistedBacktest ? formatBacktestPercent(persistedBacktest.weightedExpectancyPct) : '—', finiteNumber(persistedBacktest?.weightedExpectancyPct) >= 0 ? 'up' : 'down'],
          ['PF ponderado', formatWeightedProfitFactor(persistedBacktest), persistedBacktest && (persistedBacktest.weightedProfitFactorInfinite || persistedBacktest.weightedProfitFactor >= 1) ? 'up' : 'down'],
        ].map(([label, value, tone]) => (
          <div className="rc-kpi" key={label}>
            <div className="rc-kpi-label">{label}</div>
            <div className={`rc-kpi-value ${tone}`}>{value}</div>
          </div>
        ))}
      </section>

      <div className="rv-main-grid">
        <main className="rv-main-content">
          <section className="rc-conditions-card rv-conditions-card">
            <div className="rc-conditions-header">
              <div className="rc-conditions-title">
                <strong data-testid="text-selected-reversal-ticker">{selected?.ticker || '—'}</strong> — As 5 condições
              </div>
              {selected && <span className={`rc-action-btn rv-status ${statusClass(selected)}`}>{statusLabel(selected)}</span>}
            </div>

            <div className="rc-stock-selector rv-stock-selector" aria-label="Ativos elegíveis">
              {allItems.map(item => {
                const blocked = statusClass(item) === 'blocked';
                return (
                  <button
                    key={item.ticker}
                    type="button"
                    className={`rc-stock-pill rv-stock-pill ${statusClass(item)} ${selected?.ticker === item.ticker ? 'active' : ''}`}
                    onClick={() => setSelectedTicker(item.ticker)}
                    data-testid={`chip-reversal-${item.ticker}`}
                    title={blocked ? itemBlocks(item).map(blockLabel).join(' · ') || 'Setup bloqueado' : statusLabel(item)}
                  >
                    {blocked ? '× ' : ''}{item.ticker}
                  </button>
                );
              })}
            </div>

            {selected ? (
              <>
                 <div className={`rc-setup-alert rv-setup-alert ${statusClass(selected)}`}>
                  <strong>{setupStatusText(selected)}</strong>
                  <button
                    type="button"
                    className="rc-add-btn"
                    disabled={portfolioBlocked}
                    title={portfolioBlockReason}
                    onClick={() => setShowPortfolioModal(true)}
                  >
                    {portfolioBlocked ? 'Carteira bloqueada' : '+ Carteira'}
                  </button>
                </div>
                 <DirectionalOptionsReader
                   ticker={selected.ticker}
                   setup="reversal"
                   expanded={optionsExpanded}
                   onToggle={() => setOptionsExpanded(value => !value)}
                   onEntryViaChange={setEntryVia}
                 />
                 <div className="rv-entry-via">Entrada direcional: {entryVia === 'call' ? 'Call' : 'Ativo'}</div>

                {(selectedBlocks.length > 0 || hasUnfavorableRiskReward(selected)) && (
                  <div className="rv-block-seals" role="status">
                    {hasUnfavorableRiskReward(selected) && <span>R/R &lt; 1 — não operar</span>}
                    {selectedBlocks.map((block, index) => <span key={index}>{blockLabel(block)}</span>)}
                  </div>
                )}

                <div className="rv-risk-plan" aria-label="Plano de risco do setup">
                  <span className="rv-risk-plan-title">Plano de risco</span>
                  {[
                    ['Entrada', formatPrice(selected.entryPrice), 'entry'],
                    ['Stop', formatPrice(selected.stopPrice), 'stop'],
                    ['Alvo 1', formatPrice(selected.target1Price), 'exit'],
                    ['ATR(14)', formatPrice(selected.atr14), ''],
                    ['Risco (ATR)', formatAtr(selected.riskAtr), ''],
                    ['R/R', finiteNumber(selected.riskReward) == null ? '—' : `${Number(selected.riskReward).toFixed(2).replace('.', ',')}R`, hasUnfavorableRiskReward(selected) ? 'stop' : 'entry'],
                  ].map(([label, value, tone]) => (
                    <span className="rv-risk-plan-item" key={label}>
                      <span>{label}</span>
                      <strong className={tone}>{value}</strong>
                    </span>
                  ))}
                </div>
              </>
            ) : <div className="rv-empty">Nenhum ativo elegível.</div>}
          </section>

          {selected && (
            <section className="rc-chart-card rv-chart-card" aria-label={`Gráfico técnico de ${selected.ticker}`}>
              <ReversalSetupChart ticker={selected.ticker} setup={selected} />
            </section>
          )}
        </main>

        <aside className="rc-right rv-side" aria-label="Resumo do ativo selecionado">
          <div className="rc-panel">
            <div className="rc-panel-title">Score do setup · {selected?.ticker || '—'}</div>
            {selected ? (
              <>
                <div className="rc-score-wrap">
                  <div className="rc-score-col">
                    <ScoreCircle score={selected.score} max={selected.scoreMax ?? 100} />
                    <div className="rc-score-foot"><strong>{passedConditions}</strong> de <strong>{conditions.length || 5}</strong> condições</div>
                  </div>
                  <div className="rc-score-checklist">
                    {conditions.map((condition, index) => {
                      const pass = conditionPass(condition);
                      return (
                        <div
                          className={`rc-score-check ${pass ? 'pass' : 'fail'}`}
                          key={condition.id || condition.key || index}
                          title={`${conditionLabel(condition)} · ${conditionValue(condition)}`}
                        >
                          <span className="rc-score-check-icon">{pass ? '✓' : '○'}</span>
                          <span className="rc-score-check-label">{compactConditionLabel(condition)} ·</span>
                          <span className="rc-score-check-value">{conditionPoints(condition).replace(' pts', '')}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {portfolioBlocked && <div className="rv-portfolio-blocked">{portfolioBlockReason}</div>}
              </>
            ) : <div className="rc-empty-pos">Selecione um ativo.</div>}
          </div>
          <PositionList
            title="Posições abertas · Reversão"
            items={openPositions}
            emptyText="Nenhuma posição aberta da Reversão."
            onSelect={setSelectedTicker}
          />
          <PositionList
            title="Ordens pendentes"
            items={pendingOrders}
            emptyText="Nenhuma ordem pendente da Reversão."
            onSelect={setSelectedTicker}
          />
        </aside>
      </div>

      <section className="rc-panel rv-performance" data-testid="reversal-backtest-summary">
        <div className="rc-panel-title">
          Performance · Backtest da Reversão
          {persistedBacktest?.periodStart && persistedBacktest?.periodEnd
            ? ` · ${formatTradingDate(persistedBacktest.periodStart)}–${formatTradingDate(persistedBacktest.periodEnd)}`
            : ''}
          {persistedBacktest ? ` · ${persistedBacktest.tradingDays ?? '—'} pregões · ${persistedBacktest.tradesTotal ?? '—'} trades` : ''}
        </div>
        {persistedBacktest ? (
          <div className="rc-perf-grid rv-perf-grid">
            {[
              ['Win rate simples', finiteNumber(persistedBacktest.winRate) == null ? '—' : formatPct(persistedBacktest.winRate, false), finiteNumber(persistedBacktest.winRate) >= 50 ? 'up' : 'down'],
              ['Expectância bruta', formatBacktestPercent(persistedBacktest.expectancyPct ?? persistedBacktest.avgReturnPct), finiteNumber(persistedBacktest.expectancyPct ?? persistedBacktest.avgReturnPct) >= 0 ? 'up' : 'down'],
              ...(hasBacktestCosts ? [[netExpectancyLabel, formatBacktestPercent(netExpectancyPct), finiteNumber(netExpectancyPct) >= 0 ? 'up' : 'down']] : []),
              ...(hasBacktestCosts ? [['Custos totais', persistedBacktest.custosTotaisPct == null ? '—' : formatBacktestPercent(persistedBacktest.custosTotaisPct), 'down']] : []),
              ['PF simples', formatProfitFactor(persistedBacktest), persistedBacktest.profitFactorInfinite || persistedBacktest.profitFactor >= 1 ? 'up' : 'down'],
              ['Win rate ponderado', finiteNumber(persistedBacktest.weightedWinRate) == null ? '—' : formatPct(persistedBacktest.weightedWinRate, false), finiteNumber(persistedBacktest.weightedWinRate) >= 50 ? 'up' : 'down'],
              ['Expectância ponderada bruta', formatBacktestPercent(persistedBacktest.weightedExpectancyPct), finiteNumber(persistedBacktest.weightedExpectancyPct) >= 0 ? 'up' : 'down'],
              ...(hasBacktestCosts ? [[`Expectância ponderada líquida (${backtestCostsBps} bps/lado)`, formatBacktestPercent(weightedNetExpectancyPct), finiteNumber(weightedNetExpectancyPct) >= 0 ? 'up' : 'down']] : []),
              ['PF ponderado', formatWeightedProfitFactor(persistedBacktest), persistedBacktest.weightedProfitFactorInfinite || persistedBacktest.weightedProfitFactor >= 1 ? 'up' : 'down'],
              ['Média hold', finiteNumber(persistedBacktest.avgHold) == null ? '—' : `${Number(persistedBacktest.avgHold).toFixed(1).replace('.', ',')}d`, ''],
              ['Pior trade', finiteNumber(persistedBacktest.worstTradePct) == null ? '—' : formatPct(persistedBacktest.worstTradePct), 'down'],
            ].map(([label, value, tone]) => (
              <div className="rc-perf-cell" key={label}>
                <div className="rc-perf-label">{label}</div>
                <div className={`rc-perf-value ${tone}`}>{value}</div>
              </div>
            ))}
          </div>
        ) : <div className="rv-empty">Resumo histórico indisponível.</div>}
        <EstatisticasHonestas backtest={persistedBacktest} testId="reversal-estatisticas" />
        <ForaDaAmostra backtest={persistedBacktest} testId="reversal-fora-da-amostra" />
        <EstagioEstrategia backtest={persistedBacktest} testId="reversal-estagio" />
      </section>

      <ReversalTable items={allItems} selectedTicker={selected?.ticker} onSelect={setSelectedTicker} />

      {showPortfolioModal && selected && !portfolioBlocked && (
        <PortfolioModal
          ticker={selected.ticker}
          name={selected.name || selected.ticker}
          strategy="Reversão na Tendência"
          refs={[
            { label: 'Fechamento', value: formatPrice(selected.close) },
            { label: 'RSI(2)', value: finiteNumber(selected.rsi2)?.toFixed(1) ?? '—', highlight: true },
            { label: 'Risco / retorno', value: finiteNumber(selected.riskReward) == null ? '—' : `${Number(selected.riskReward).toFixed(2).replace('.', ',')}R` },
          ]}
          defaultEntryPrice={selected.entryPrice ?? selected.close}
          suggestedStop={selected.stopPrice}
          suggestedStopLabel="stop do setup"
          suggestedTarget={selected.target1Price}
          suggestedTargetLabel="alvo 1 do setup"
          enforceMinRiskReward
          pendingOrder={selected.status === 'ARMADO'}
          requiresOverride={backtestBlocked}
          strategyHealth={backtestGate}
          onClose={() => setShowPortfolioModal(false)}
          onSave={async formData => {
            const pendingOrder = selected.status === 'ARMADO';
            await addPosition({
              ticker: selected.ticker,
              entry_via: entryVia,
              name: selected.name || selected.ticker,
              strategy: 'Reversão na Tendência',
              order_status: pendingOrder ? 'pending' : 'open',
              trigger_price: pendingOrder ? selected.entryPrice : undefined,
              armed_date: pendingOrder ? selected.referenceDate : undefined,
              ...formData,
              entry_price: pendingOrder ? selected.entryPrice : formData.entry_price,
            });
            setShowPortfolioModal(false);
            loadPortfolio();
          }}
        />
      )}
    </div>
  );
}